import { LitElement, css, html, nothing } from "lit";
import { defineChromeElement, pressFeedback } from "../chrome/ChromeElement";
import type { SystemModalBindings, SystemModalPort } from "./SystemModalPort";

/** The window's authored size in design units, and the rows inside it measured from its top. */
const WINDOW = 868.5;
const ROWS = { title: { top: 99.55, height: 67.6 }, message: { top: 229.07, height: 345 }, primary: { top: 589.65 }, secondary: { top: 707.25 } } as const;
const BUTTON = { width: 620.4, height: 85 } as const;

/**
 * `<slot-system-modal>`: the system modal (rejected spin, transport failure, restore offer, replay
 * failed) as a DOM panel in the chrome root. A full-screen blocker takes every pointer event while
 * open; the dim shows only when the modal is dismissible. The buttons press through the chrome
 * root's feedback event (click cue + soft haptic).
 */
export class SystemModalElement extends LitElement implements SystemModalPort {
  public static override properties = {
    open: { type: Boolean, reflect: true },
    heading: { type: String },
    message: { type: String },
    primaryText: { type: String },
    secondaryText: { type: String },
    secondaryVisible: { type: Boolean },
    dismissible: { type: Boolean },
  };

  public declare open: boolean;
  /** `heading`, not `title`: the native `title` property is the tooltip. */
  public declare heading: string;
  public declare message: string;
  public declare primaryText: string;
  public declare secondaryText: string;
  public declare secondaryVisible: boolean;
  public declare dismissible: boolean;

  private bindings: SystemModalBindings | null = null;

  public constructor() {
    super();
    this.open = false;
    this.heading = "";
    this.message = "";
    this.primaryText = "";
    this.secondaryText = "";
    this.secondaryVisible = false;
    this.dismissible = true;
  }

  public static override styles = css`
    :host { display: none; position: absolute; inset: 0; pointer-events: auto; }
    :host([open]) { display: block; }
    .blocker { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
    .dim { position: absolute; inset: 0; background: var(--slot-modal-dim); }
    .window {
      position: relative;
      width: ${WINDOW}px;
      height: ${WINDOW}px;
      border-radius: 16px;
      background: var(--slot-modal-window);
      color: var(--slot-modal-text);
      transform: scale(min(calc(var(--slot-modal-max-width, ${WINDOW}) / ${WINDOW}), calc(var(--slot-design-width, ${WINDOW}) / ${WINDOW})));
    }
    .row { position: absolute; left: 50%; transform: translateX(-50%); display: flex; align-items: center; justify-content: center; text-align: center; }
    .title { top: ${ROWS.title.top}px; width: 769.1px; height: ${ROWS.title.height}px; font-size: 45px; font-weight: 700; line-height: 1; overflow: hidden; }
    .message { top: ${ROWS.message.top}px; width: 769.1px; height: ${ROWS.message.height}px; font-size: 45px; font-weight: 700; line-height: 48px; overflow: hidden; white-space: pre-wrap; }
    button {
      width: ${BUTTON.width}px; height: ${BUTTON.height}px; border: 0; border-radius: 8px; padding: 0;
      font: inherit; font-size: 35px; font-weight: 500; color: var(--slot-button-text); text-shadow: 0 0 2px #000;
      cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent;
    }
    button:hover { filter: brightness(0.83); }
    button:active { filter: none; }
    .primary { top: ${ROWS.primary.top}px; background: var(--slot-modal-primary); }
    .secondary { top: ${ROWS.secondary.top}px; background: var(--slot-modal-secondary); }
  `;

  public bind(bindings: SystemModalBindings): void {
    this.bindings = bindings;
  }

  public setOpen(open: boolean): void {
    this.open = open;
  }

  public setTitle(text: string): void {
    this.heading = text;
  }

  public setMessage(text: string): void {
    this.message = text;
  }

  public setPrimaryButtonText(text: string): void {
    this.primaryText = text;
  }

  public setSecondaryButtonText(text: string): void {
    this.secondaryText = text;
  }

  public setSecondaryVisible(visible: boolean): void {
    this.secondaryVisible = visible;
  }

  public setBackdropDismissEnabled(enabled: boolean): void {
    this.dismissible = enabled;
  }

  protected override render() {
    return html`
      <div class="blocker" @click=${this.onBackdropClick}>
        ${this.dismissible ? html`<div class="dim"></div>` : nothing}
        <div class="window" role="dialog" aria-modal="true" aria-labelledby="title" @click=${stop}>
          <div class="row title" id="title" data-part="title">${this.heading}</div>
          <div class="row message" data-part="message">${this.message}</div>
          <button class="row primary" data-part="primary" @pointerdown=${pressFeedback} @click=${() => this.bindings?.onPrimary()}>${this.primaryText}</button>
          ${this.secondaryVisible
            ? html`<button class="row secondary" data-part="secondary" @pointerdown=${pressFeedback} @click=${() => this.bindings?.onSecondary()}>${this.secondaryText}</button>`
            : nothing}
        </div>
      </div>
    `;
  }

  private readonly onBackdropClick = (): void => {
    this.bindings?.onBackdrop();
  };
}

function stop(event: Event): void {
  event.stopPropagation();
}

export const SYSTEM_MODAL_TAG = "slot-system-modal";

/** Defines the element once (a module may be evaluated once per page, but tests re-import). */
export function defineSystemModalElement(): void {
  defineChromeElement(SYSTEM_MODAL_TAG, SystemModalElement);
}
